/**
 * Analytics helpers (TZ §12).
 *
 * IMPORTANT: never call Telegram.WebApp.sendData from here.
 * sendData closes the Mini App and posts "Data from the Open button was
 * transferred to the bot" — that broke Open from reply keyboard / menu.
 * Events stay in a local buffer (and console in DEV).
 */

export type AnalyticsEventName =
  | "web_app_opened"
  | "workout_started"
  | "workout_session_restored"
  | "workout_completed"
  | "workout_exercise_completed"
  | "workout_exercises_bulk_replaced"
  | "program_started"
  | "exercise_media_played"
  | "onboarding_completed"
  | "activation_completed"
  | "set_logged"
  | "nutrition_logged"
  | "nutrition_barcode_selected"
  | "nutrition_label_recognized"
  | "ai_message_sent"
  | "reentry_shown"
  | "habit_checked"
  | "first_plan_viewed"
  | "schedule_saved"
  | "workout_assigned_earlier"
  | "measurement_saved"
  | "nutrition_opened"
  | "faq_opened"
  | "activation_checklist_shown"
  | "activation_checklist_item_completed"
  | "activation_checklist_completed"
  | "activation_checklist_snoozed"
  | "activation_checklist_dismissed"
  | "plus_gate_viewed"
  | "plus_feature_opened"
  | "progress_analytics_depth_changed"
  | "subscription_tier_changed"
  | "beta_plus_notice_seen"
  | "beta_plus_notice_dismissed";

export type AnalyticsPayload = Record<string, string | number | boolean | null | undefined>;

const BUFFER_KEY = "fitness_analytics_buffer";

function pushLocal(event: string, payload: AnalyticsPayload): void {
  try {
    const raw = localStorage.getItem(BUFFER_KEY);
    const list = raw ? (JSON.parse(raw) as unknown[]) : [];
    list.push({ event, payload, ts: new Date().toISOString() });
    // keep last 50
    localStorage.setItem(BUFFER_KEY, JSON.stringify(list.slice(-50)));
  } catch {
    // ignore quota / private mode
  }
}

/** Record analytics event locally (does not close Telegram Mini App). */
export function trackEvent(event: AnalyticsEventName, payload: AnalyticsPayload = {}): void {
  pushLocal(event, payload);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("fitness:analytics-event", { detail: { event } }));
  }

  if (import.meta.env.DEV) {
    console.debug("[analytics]", event, payload);
  }
}

export function getAnalyticsBuffer(): unknown[] {
  try {
    const raw = localStorage.getItem(BUFFER_KEY);
    return raw ? (JSON.parse(raw) as unknown[]) : [];
  } catch {
    return [];
  }
}
